import {
    UploadToOneDriveText,
    OneDrivePersonalText,
} from './convertClassicToCloudyContextMenuItemBuilder.locstring.json';
import loc, { format } from 'owa-localize';
import isMenuActionSupported from '../../utils/isMenuActionSupported';
import { IContextualMenuItem } from 'office-ui-fabric-react/lib/ContextualMenu';
import { getCompanyName } from 'owa-attachment-data';
import { getAttachmentSharingInformationForODB } from 'owa-attachment-full-data';
import { AttachmentConstants, AttachmentModel } from 'owa-attachment-model-store';
import getAttachment from 'owa-attachment-model-store/lib/selectors/getAttachment';
import { PreviewContext } from 'owa-attachment-select-actions';
import AttachmentPermissionLevel from 'owa-service/lib/contract/AttachmentPermissionLevel';
import AttachmentType from 'owa-service/lib/contract/AttachmentType';
import ItemId from 'owa-service/lib/contract/ItemId';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';

import { isSxSDisplayedForAttachment, lazyCanCloseSxS, lazyForceCloseSxS } from 'owa-sxs-store';
import {
    AttachmentFullViewState,
    AttachmentMenuAction,
    convertClassicToCloudy,
    getSaveToCloudEstimatedCompleteTimeInMS,
    setActionCompletePercent,
    startFakeProgressIndicator,
    stopFakeProgressIndicator,
} from 'owa-attachment-full-data';

export default function convertClassicToCloudyContextMenuItemBuilder(
    attachment: AttachmentFullViewState,
    providerType: string,
    targetWindow: Window,
    sxsId: string,
    isConsumer: boolean,
    attachmentOriginalUrl: string,
    permissionLevel: AttachmentPermissionLevel,
    previewContext: PreviewContext,
    parentItemId: ItemId,
    replaceAttachmentHandler: (
        attachmentViewState: AttachmentFullViewState,
        attachmentType: AttachmentType,
        isReadOnly: boolean,
        uploadCompleted: boolean
    ) => void,
    AttachLongPathName?: string,
    container?: HTMLElement,
    moveAttachmentToInlineHandler?: (
        attachment: AttachmentFullViewState,
        contentId: string
    ) => void,
    referenceAttachmentsSupported?: () => boolean
): IContextualMenuItem | null {
    if (
        !isMenuActionSupported(AttachmentMenuAction.ConvertClassicToCloudy, attachment) ||
        !attachment.strategy.isConvertClassicToCloudySupported ||
        (referenceAttachmentsSupported && !referenceAttachmentsSupported())
    ) {
        return null;
    }

    const attachmentModel: AttachmentModel = getAttachment(attachment.attachmentId);

    // TODO VSO 35586: support convertClassicToCloudy for item attachments
    if (
        !attachmentModel ||
        attachmentModel.model.__type === AttachmentConstants.attachmentTypes.ITEM_ATTACHMENT_TYPE
    ) {
        return null;
    }

    return {
        name: getConvertClassicToCloudyContextMenuName(),
        key: 'ConvertClassicToCloudy',
        onClick: () => {
            convertClassicToCloudyInternal(
                attachment,
                parentItemId,
                replaceAttachmentHandler,
                sxsId
            );
        },
    };
}

// exported for testing only
export async function convertClassicToCloudyInternal(
    attachment: AttachmentFullViewState,
    parentItemId: ItemId,
    replaceAttachmentHandler: (
        attachmentViewState: AttachmentFullViewState,
        attachmentType: AttachmentType,
        isReadOnly: boolean,
        uploadCompleted: boolean
    ) => void,
    sxsId: string
) {
    if (isSxSDisplayedForAttachment(attachment.attachmentId, sxsId)) {
        (await (await lazyCanCloseSxS.import())(sxsId)) ||
            (await lazyForceCloseSxS.import())('convertClassicToCloudy', sxsId);
    }
    startFakeProgressIndicator(
        getSaveToCloudEstimatedCompleteTimeInMS(attachment.attachmentId),
        0.1,
        (completePercent: number) => {
            setActionCompletePercent(attachment, completePercent);
        },
        attachment.attachmentId
    );
    convertClassicToCloudy(
        attachment,
        parentItemId,
        (succeeded: boolean, attachmentType: AttachmentType) => {
            if (succeeded) {
                replaceAttachmentHandler(
                    attachment,
                    attachmentType,
                    false /* isReadOnly */,
                    true /* uploadCompleted */
                );

                // We must call getAttachment after replaceAttachmentHandler so that we get the
                // sharing information for the new attachment, not the one that was already replaced.
                const attachmentModel: AttachmentModel = getAttachment(attachmentType.AttachmentId);
                getAttachmentSharingInformationForODB(attachmentModel);
            } else {
                stopFakeProgressIndicator(attachment.attachmentId, succeeded);
            }
        }
    );
}

function getConvertClassicToCloudyContextMenuName(): string {
    return format(
        loc(UploadToOneDriveText),
        isConsumer() ? loc(OneDrivePersonalText) : getCompanyName()
    );
}
